import { createSelector } from '@reduxjs/toolkit';
import { ConfirmationToken as StripeConfirmationToken } from '@stripe/stripe-js';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';
import Stripe from 'stripe';

import { notify } from 'src/clients/ApiService';
import { AppAPIName } from 'src/constants';
import {
  InvoiceSchema,
  InvoiceTemplateSchema,
  LineItemsSchema,
  PaymentsAddressSchema,
} from 'src/routes/payments/invoices/types';
import { recursiveLoad } from 'src/services/recursiveLoad';
import { RootState } from 'src/store';
import { selectAllCompaniesMap } from 'src/store/clients/types';
import { AppDispatch } from 'src/store/configureStore';
import { StripeElementsLayout } from 'src/types/payment';
import { API } from 'src/utils/AmplifyApiUtils';
import { arrayToMap } from 'src/utils/array';
import { ensureApiError } from 'src/utils/Errors';
import { convertAmountToCents } from 'src/utils/InvoiceUtils';

import { settleQueryFulfilled } from './settleQueryFulfilled';

import { ApiMethods, ApiTags, appAPI } from '.';

type PatchCollection = {
  undo: () => void;
};

export const MIN_LINE_ITEM_AMOUNT = 0.5;
export const MAX_LINE_ITEM_AMOUNT = 100000;
export const MAX_LINE_ITEM_QUANTITY = 1000000;
export const MAX_TOTAL_AMOUNT = 999999;
export const MIN_LINE_ITEM_AMOUNT_ERROR = `Total must be at least ${MIN_LINE_ITEM_AMOUNT}.`;
export const MAX_TOTAL_AMOUNT_ERROR = `Total must be at most ${new Intl.NumberFormat(
  'en-US',
).format(MAX_TOTAL_AMOUNT)}.`;

export enum Currencies {
  USD = 'USD',
}

export enum PaymentSourceType {
  Account = 'account',
  ApplePay = 'apple_pay',
  AlipayAccount = 'alipay_account',
  BankAccount = 'bank_account',
  BitcoinReceiver = 'bitcoin_receiver',
  Card = 'card',
  GooglePay = 'google_pay',
  Source = 'source',
}

export enum StripeCardStatus {
  New = 'new',
  Validated = 'validated',
  Verified = 'verified',
  VerificationFailed = 'verification_failed',
  Errored = 'errored',
}
export enum InvoiceCollectionMethod {
  SendInvoice = 'sendInvoice',
  ChargeAutomatically = 'chargeAutomatically',
}

export enum InvoiceIncompleteReasons {
  NO_BANKING_INFO = 'no-banking-info',
  NO_PAYMENT_METHOD = 'no-payment-method',
  NON_USD_SUBSCRIPTION = 'non-usd-subscription',
}

export enum LineItemType {
  Product = 'product',
  OneTime = 'oneTime',
}

export enum InvoiceStatus {
  Draft = 'draft',
  Open = 'open',
  // this is UI only status.
  // it's derived from the invoice status and payment status
  // if the invoice is open and payment is processing
  Processing = 'processing',
  Overdue = 'overdue',

  Void = 'void',
  Uncollectible = 'uncollectible',
  Paid = 'paid',
}

export enum InvoiceLatestRefundStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

export enum PaymentStatus {
  Retrying = 'retrying',
  PastDue = 'pastDue',
  Cancelled = 'cancelled',
  Success = 'succeeded',
  Pending = 'pending',
  Processing = 'processing',
  Failed = 'failed',
}

export enum Origin {
  PaymentLink = 'payment_link',
  ProductStore = 'product_store',
  Internal = 'internal',
}

export enum PaymentMethodType {
  BankAccount = 'bankAccount',
  CreditCard = 'creditCard',
}

export enum StripeEntityType {
  Invoice = 'stripe_invoice',
  Subscription = 'stripe_subscription',
}

export enum InvoicePreviewType {
  Summary = 'summary',
  Checkout = 'checkout',
  Invoice = 'invoice',
  Email = 'email',
}

export type PaymentOptions = {
  allowPaymentViaACH: boolean;
  allowPaymentViaCC: boolean;
  absorbTransactionFees?: boolean;
  absorbACHFees?: boolean;
  absorbCreditCardFee?: boolean;
  allowPlaidSetup?: boolean;
};

export const PAYMENT_METHOD_MAPPER = {
  bankAccount: 'Bank Account',
  creditCard: 'Credit Card',
};

export interface InvoiceDetailOptions {
  id: string;
  amountDue: number;
  fees: number;
  tax: number;
  currency: string;
  invoiceNumber: string;
  dueDate: string;
  dateOfIssue: string;
  description?: string;
  collectionMethod: string;
  attachmentKeys: string[];
  fileKey?: string;
  identityId?: string;
  ignoreFees: boolean;
  taxPercentage?: string;
  isSubscriptionInvoice?: boolean;
  subscriptionId?: string;
  interval?: string;
  lineItems: LineItem[];
  feeLabel?: string;
  layout: StripeElementsLayout;
}

export type LineItem = {
  id: string;
  productId?: string;
  priceId?: string;
  quantity: string;
  amount?: string;
  description?: string;
  type: LineItemType;
};

export type PaymentPreferences = {
  paymentMethodTypes: PaymentMethodType[];
  absorbTransactionFee: PaymentMethodType[];
};

type QuickBooksData = {
  invoiceId: string;
  paymentId: string;
};

export enum RecipientType {
  Client = 'client',
  Company = 'company',
}

type FeeItems = {
  amount: number;
  type: 'string';
};

export type Fee = {
  isFeeAbsorbed: boolean;
  absorbedAmount: number;
  chargedAmount: number;
  totalAmount: number;
  items: FeeItems;
};

export interface Payment {
  message: string;
  status: PaymentStatus;
  invoiceId: string;
  portalId: string;
  amount: number;
  paymentMethod: PaymentMethodType;
  last4Digits: string;
  stripeChargeId: string;
  chargedAt: string;
  createdAt: string;
  updatedAt: string;
  fee: Fee | undefined;
  ref: string;
  intentId: string;
  intent?: Stripe.PaymentIntent;
}

export type PayInvoiceInput = {
  invoiceId: string;
  sourceId?: string;
  manual: boolean;
  confirmationToken?: StripeConfirmationToken;
};

type InvoiceMetaData = {
  total: number;
  currency: string;
  taxPercentage: number;
  taxAmount: number;
  memo: string;
  attachmentKeys: string[] | null;
  lineItems: LineItemsSchema;
};

export type InvoiceTemplate = {
  id?: string;
  name?: string;
} & InvoiceMetaData;

export type Invoice = {
  id: string;
  number: string;
  subscriptionId?: string | null;
  recipientId: string;
  clientId?: string;
  companyId?: string;
  status: InvoiceStatus;
  dueDate: string;
  paymentStatus: PaymentStatus;
  collectionMethod: InvoiceCollectionMethod;
  paymentPreferences: PaymentPreferences;
  nextPaymentAttemptDate?: Date | null;
  amountPaid: number;
  paidManually?: boolean;
  fileUrl: string;
  quickBooksData: QuickBooksData;
  sentDate?: string | null;
  defaultListIndexPkey: string;
  payment: Payment[] | null;
  createdAt: string;
  ref: string;
  fileKey?: string;
  identityId?: string;
  paymentSuccessDate?: string;
  interval?: string;
  receiptKey?: string;
  receiptNumber?: string;
  sourcePaymentLinkId?: string;
  origin?: Origin;
  address?: PaymentsAddressSchema;
  latestRefundStatus?: InvoiceLatestRefundStatus;
} & InvoiceMetaData;

type DuplicateInvoiceInput = InvoiceSchema & {
  status: InvoiceStatus.Draft;
};

type UpdateInvoiceInput = InvoiceSchema & {
  id: string;
};

export type SendDraftInvoicePayload = {
  invoice: UpdateInvoiceInput & {
    status: InvoiceStatus.Open;
  };
};

export type LoadInvoicesQueryOptions = {
  // Client queries
  clientId?: string;
  includeCompanyInvoices?: boolean;

  // Company queries
  companyId?: string;

  // Subscription based queries
  subscriptionId?: string;
};

async function loadInvoices(queryOptions?: LoadInvoicesQueryOptions) {
  const { clientId, companyId, subscriptionId } = queryOptions || {};
  const queryStringParameters: {
    limit: number;
    nextToken?: string;
    clientId?: string;
    companyId?: string;
    subscriptionId?: string;
    includeCompanyInvoices?: boolean;
  } = {
    limit: 1000,
  };
  if (clientId) {
    queryStringParameters['clientId'] = clientId;
  }
  if (queryOptions?.includeCompanyInvoices) {
    queryStringParameters['includeCompanyInvoices'] = true;
  }
  if (companyId) {
    queryStringParameters['companyId'] = companyId;
  }
  if (subscriptionId) {
    queryStringParameters['subscriptionId'] = subscriptionId;
  }

  return recursiveLoad<Invoice>(
    (currentQueryOptions) =>
      API.get(AppAPIName, '/invoices', currentQueryOptions),
    queryStringParameters,
  );
}

async function loadInvoiceTemplates() {
  const queryStringParameters: {
    limit: number;
    nextToken?: string;
  } = {
    limit: 1000,
  };
  return recursiveLoad<InvoiceTemplate>(
    (currentQueryOptions) =>
      API.get(AppAPIName, '/invoice-templates', currentQueryOptions),
    queryStringParameters,
  );
}

/**
 * This method is used to get the success message for the invoice create and update
 * actions. It takes into consideration many scenarios
 * - If the invoice is charge automatically and the status is open, but it is failing to charge
 * - If the invoice is charge automatically and the status is open, and it is successfully charged
 */
const getInvoiceActionSuccessMessage = (invoiceInput: {
  collectionMethod: InvoiceCollectionMethod;
  status: PaymentStatus | undefined;
  id?: string;
}) => {
  const { id, collectionMethod, status } = invoiceInput;
  const actionName = id ? 'updated' : 'created';
  let invoiceSuccessMessage = `Invoice has been ${actionName}.`;
  if (
    collectionMethod === InvoiceCollectionMethod.ChargeAutomatically &&
    !(status === PaymentStatus.Success || status === PaymentStatus.Processing)
  ) {
    // for charge automatically invoices, we expect the invoice to be be in paid status
    // but if it's not, then it means that the charge failed for some reason
    // and the invoice is still open, so we communicate to that to the user as well
    invoiceSuccessMessage = `Invoice has been ${actionName} but failed to charge automatically`;
  }
  return invoiceSuccessMessage;
};

const extractPaymentStatus = (invoice: Invoice) => {
  if (invoice.payment && invoice.payment.length > 0) {
    const paymentStatus = invoice.payment?.[invoice.payment.length - 1]?.status;
    return paymentStatus;
  }
  return;
};

export const formatLineItems = (lineItems: LineItem[]) => {
  return lineItems.map((item) => {
    const isOneoffLineItem = item.type === LineItemType.OneTime;
    const itemVal = {
      id: item.id,
      quantity: Number(item.quantity),
      amount: convertAmountToCents(Number(item.amount)),
      description: item.description,
      type: item.type,
    };
    if (isOneoffLineItem) {
      return itemVal;
    }
    return {
      ...itemVal,
      productId: item.productId,
      priceId: item.priceId,
    };
  });
};

type UpdateInvoiceTemplateInput = {
  id: string;
} & InvoiceTemplateSchema;

export const invoicesApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    deleteInvoice: build.mutation<string, { id: string; recipientId: string }>({
      query: (params) => ({
        path: `/invoices/${params.id}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted(param, { dispatch, queryFulfilled, getState }) {
        const state = getState() as unknown as RootState;
        const { companies, clients } = state.clients;
        const companiesMap = arrayToMap(companies, 'id');
        const company = companiesMap.get(param.recipientId);
        const clientInvoicePatches: PatchCollection[] = [];

        if (company) {
          const companyClientsIds = clients
            .filter(
              (client) =>
                // TODO:(enable_many_companies) remove the companyId check once we enable many companies
                client.fields.companyId === param.recipientId ||
                client.fields.companyIds?.includes(param.recipientId),
            )
            .map((client) => client.id);
          for (const clientId of companyClientsIds) {
            clientInvoicePatches.push(
              dispatch(
                invoicesApi.util.updateQueryData(
                  'getInvoicesByRecipientId',
                  {
                    clientId,
                    companyId: company.id,
                    includeCompanyInvoices: Boolean(
                      clientId && param.recipientId,
                    ),
                  },
                  (draft) =>
                    draft.filter((inv: Invoice) => inv.id !== param.id),
                ),
              ),
            );
          }
        } else {
          clientInvoicePatches.push(
            dispatch(
              invoicesApi.util.updateQueryData(
                'getInvoicesByRecipientId',
                {
                  clientId: param.recipientId,
                  companyId: undefined,
                  includeCompanyInvoices: false,
                },
                (draft) => draft.filter((inv: Invoice) => inv.id !== param.id),
              ),
            ),
          );
        }

        const companyInvoicePatch = dispatch(
          invoicesApi.util.updateQueryData(
            'getInvoicesByRecipientId',
            {
              clientId: undefined,
              companyId: param.recipientId,
              includeCompanyInvoices: false,
            },
            (draft) => draft.filter((inv: Invoice) => inv.id !== param.id),
          ),
        );

        const listInvoicePatch = dispatch(
          invoicesApi.util.updateQueryData('getInvoices', undefined, (draft) =>
            draft.filter((inv: Invoice) => inv.id !== param.id),
          ),
        );

        notify({
          status: 'success',
          successMessage: 'Invoice is deleted successfully.',
          dispatch,
        });

        try {
          await queryFulfilled;
        } catch (error) {
          listInvoicePatch.undo();
          companyInvoicePatch?.undo();
          clientInvoicePatches.forEach((patch) => patch.undo());
          notify({
            status: 'error',
            errorMessage: 'Invoice can not be deleted.',
            error,
            dispatch,
          });
        }
      },
    }),
    voidInvoice: build.mutation<Invoice, { id: string; recipientId: string }>({
      query: (params) => ({
        path: `/invoices/${params.id}/void`,
        method: ApiMethods.post,
        options: {},
      }),

      async onQueryStarted(param, { dispatch, queryFulfilled, getState }) {
        const settled = await settleQueryFulfilled(
          queryFulfilled,
          'voidInvoice',
        );
        if (!settled.ok) {
          notify({
            status: 'error',
            errorMessage: 'Invoice can not be void.',
            error: settled.error,
            dispatch,
          });
          return;
        }

        const state = getState() as unknown as RootState;
        const invoice = settled.result.data;

        updateInvoiceQuery(invoice, dispatch, state);

        dispatch(
          invoicesApi.util.updateQueryData(
            'getInvoiceById',
            param.id,
            (draft) => {
              if (draft) {
                draft.status = InvoiceStatus.Void;
              }
            },
          ),
        );

        notify({
          status: 'success',
          successMessage: 'Invoice is voided successfully.',
          dispatch,
        });
      },
    }),

    createInvoice: build.mutation<Invoice, { invoice: InvoiceSchema }>({
      query: ({ invoice }) => {
        const baseBody = {
          ...invoice,
          taxPercentage: Number(invoice.taxPercentage),
          lineItems: formatLineItems(invoice.lineItems),
          dueDate:
            invoice.collectionMethod === InvoiceCollectionMethod.SendInvoice
              ? invoice.dueDate
              : null,
        };

        // Remove recipientId, it doesn't need to be passed in.
        // It was previously used without many companies. Now we pass
        // clientId and companyId instead.
        const body =
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (({ recipientId, ...rest }) => rest)(baseBody);

        return {
          path: '/invoices',
          method: ApiMethods.post,
          options: {
            body,
          },
        };
      },
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        let updateInvoicePatches;
        try {
          const result = await queryFulfilled;
          const invoice = result.data;
          // This is unforunate type casting by RTK docs mention this way to type `getState`
          const state = getState() as unknown as RootState;
          if (invoice) {
            updateInvoicePatches = updateInvoiceQuery(invoice, dispatch, state);
          }

          const invoiceSuccessMessage = getInvoiceActionSuccessMessage({
            collectionMethod: payload.invoice.collectionMethod,
            status: extractPaymentStatus(invoice),
          });
          notify({
            status: 'success',
            successMessage: invoiceSuccessMessage,
            dispatch,
          });
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error?.message;
          updateInvoicePatches?.listInvoicesPatch?.undo();
          updateInvoicePatches?.clientInvoicePatches?.forEach((patch) =>
            patch.undo(),
          );
          updateInvoicePatches?.companyInvoicePatch?.undo();
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Error occurs while paying invoice.',
            dispatch,
          });
        }
      },
    }),
    updateInvoice: build.mutation<Invoice, UpdateInvoiceInput>({
      query: (invoice) => ({
        path: `/invoices/${invoice.id}`,
        method: ApiMethods.put,
        options: {
          body: {
            ...invoice,
            taxPercentage: Number(invoice.taxPercentage),
            lineItems: formatLineItems(invoice.lineItems),
            dueDate:
              invoice.collectionMethod === InvoiceCollectionMethod.SendInvoice
                ? invoice.dueDate
                : null,
          },
        },
      }),
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        let updateInvoicePatches;
        try {
          const result = await queryFulfilled;
          const invoice = result.data;
          // This is unforunate type casting by RTK docs mention this way to type `getState`
          const state = getState() as unknown as RootState;
          if (invoice) {
            updateInvoicePatches = updateInvoiceQuery(invoice, dispatch, state);
          }

          const invoiceSuccessMessage = getInvoiceActionSuccessMessage({
            collectionMethod: payload.collectionMethod,
            status: extractPaymentStatus(invoice),
            id: payload.id,
          });

          notify({
            status: 'success',
            successMessage: invoiceSuccessMessage,
            dispatch,
          });
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error?.message;
          updateInvoicePatches?.listInvoicesPatch?.undo();
          updateInvoicePatches?.clientInvoicePatches?.forEach((patch) =>
            patch.undo(),
          );
          updateInvoicePatches?.companyInvoicePatch?.undo();
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Error occurs while paying invoice.',
            dispatch,
          });
        }
      },
    }),
    sendDraftInvoice: build.mutation<Invoice, SendDraftInvoicePayload>({
      query: ({ invoice }) => {
        const baseBody = {
          ...invoice,
          clientId: invoice.clientId,
          companyId: invoice.companyId,
          taxPercentage: Number(invoice.taxPercentage),
          lineItems: invoice.lineItems,
          dueDate:
            invoice.collectionMethod === InvoiceCollectionMethod.SendInvoice
              ? invoice.dueDate
              : null,
        };

        // Remove recipientId, it doesn't need to be passed in.
        // We always pass clientId and companyId instead.
        const body =
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          (({ recipientId, ...rest }) => rest)(baseBody);

        return {
          path: `/invoices/${invoice.id}`,
          method: ApiMethods.put,
          options: {
            body,
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        let updateInvoicePatches;
        try {
          const result = await queryFulfilled;
          const invoice = result.data;
          // This is unforunate type casting by RTK docs mention this way to type `getState`
          const state = getState() as unknown as RootState;
          if (invoice) {
            updateInvoicePatches = updateInvoiceQuery(invoice, dispatch, state);
          }

          notify({
            status: 'success',
            successMessage: 'Invoice sent successfully.',
            dispatch,
          });
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error?.message;
          updateInvoicePatches?.listInvoicesPatch?.undo();
          updateInvoicePatches?.clientInvoicePatches?.forEach((patch) =>
            patch.undo(),
          );
          updateInvoicePatches?.companyInvoicePatch?.undo();
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Error occurs while sending invoice.',
            dispatch,
          });
        }
      },
    }),
    // Duplicate invoice uses the same endpoint as create invoice but with a 'draft' status
    duplicateInvoice: build.mutation<
      Invoice,
      { invoice: DuplicateInvoiceInput }
    >({
      query: ({ invoice }) => {
        const baseBody = {
          ...invoice,
          taxPercentage: Number(invoice.taxPercentage),
          // note that line items are not formatted here.
          // we already have the right format from the source invoice being
          // duplicated
          lineItems: invoice.lineItems,
          dueDate:
            invoice.collectionMethod === InvoiceCollectionMethod.SendInvoice
              ? invoice.dueDate
              : null,
        };
        return {
          path: '/invoices',
          method: ApiMethods.post,
          options: {
            body: baseBody,
          },
        };
      },
      async onQueryStarted(_, { dispatch, queryFulfilled, getState }) {
        let updateInvoicePatches;
        try {
          const result = await queryFulfilled;
          const invoice = result.data;
          // This is unforunate type casting by RTK docs mention this way to type `getState`
          const state = getState() as unknown as RootState;
          if (invoice) {
            updateInvoicePatches = updateInvoiceQuery(invoice, dispatch, state);
          }

          notify({
            status: 'success',
            successMessage: 'Invoice has been created.',
            dispatch,
          });
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error?.message;
          updateInvoicePatches?.listInvoicesPatch?.undo();
          updateInvoicePatches?.clientInvoicePatches?.forEach((patch) =>
            patch.undo(),
          );
          updateInvoicePatches?.companyInvoicePatch?.undo();
          notify({
            status: 'error',
            errorMessage: errorMessage || 'Error occurs while paying invoice.',
            dispatch,
          });
        }
      },
    }),
    getInvoices: build.query<Invoice[], void>({
      queryFn: async () => {
        // fetch invoices with pagination
        const response = await loadInvoices({
          includeCompanyInvoices: true,
        });
        return {
          data: response.data.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        };
      },
      providesTags: [ApiTags.invoices],
    }),
    getInvoicesBySubscriptionId: build.query<Invoice[], string>({
      queryFn: async (subscriptionId) => {
        // fetch invoices with pagination
        const response = await loadInvoices({
          subscriptionId,
        });
        return {
          data: response.data.sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          ),
        };
      },
      providesTags: [ApiTags.invoices],
    }),
    getInvoicesByRecipientId: build.query<Invoice[], LoadInvoicesQueryOptions>({
      queryFn: async (queryOptions) => {
        // fetch invoices with pagination
        const response = await loadInvoices(queryOptions);
        return {
          data: response.data.sort(
            (a, b) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          ),
        };
      },
      providesTags: [ApiTags.clientInvoices],
    }),
    getInvoiceById: build.query<Invoice, string>({
      query: (invoiceId) => ({
        path: `/invoices/${invoiceId}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [ApiTags.invoices],
    }),

    payInvoice: build.mutation<Invoice, PayInvoiceInput>({
      query: (invoice) => ({
        path: `/invoices/${invoice.invoiceId}/pay`,
        method: ApiMethods.post,
        options: {
          body: {
            ...invoice,
          },
        },
      }),

      async onQueryStarted(param, { dispatch, queryFulfilled, getState }) {
        let updateInvoicePatches;
        try {
          const result = await queryFulfilled;
          const invoice = result.data;
          const state = getState() as unknown as RootState;
          updateInvoicePatches = updateInvoiceQuery(invoice, dispatch, state);

          dispatch(
            invoicesApi.util.updateQueryData(
              'getInvoiceById',
              param.invoiceId,
              (draft) => {
                if (result.data) {
                  Object.assign(draft, result.data);
                }
              },
            ),
          );

          // although API returns 200, it might not meet the success criteria.
          // For example, when the payment is still pending, the invoice is not paid yet.
          // Check invoice status to see if it's paid.
          if (invoice.status === InvoiceStatus.Paid) {
            notify({
              status: 'success',
              successMessage: 'Invoice has been paid',
              dispatch,
            });
          }
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          updateInvoicePatches?.listInvoicesPatch?.undo();
          updateInvoicePatches?.clientInvoicePatches?.forEach((patch) =>
            patch.undo(),
          );
          updateInvoicePatches?.companyInvoicePatch?.undo();
          notify({
            status: 'error',
            errorMessage: errorMessage || 'This invoice could not be paid.',
            dispatch,
          });
        }
      },
    }),
    createInvoiceTemplate: build.mutation<
      InvoiceTemplate,
      InvoiceTemplateSchema
    >({
      query: (invoiceTemplate) => ({
        path: '/invoice-templates',
        method: ApiMethods.post,
        options: {
          body: {
            ...invoiceTemplate,
            taxPercentage: Number(invoiceTemplate.taxPercentage),
            lineItems: formatLineItems(invoiceTemplate.lineItems),
          },
        },
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const result = await queryFulfilled;

          dispatch(
            invoicesApi.util.updateQueryData(
              'listInvoiceTemplates',
              undefined,
              (draft) => {
                draft.unshift(result.data);
              },
            ),
          );
          if (result.data) {
            notify({
              status: 'success',
              successMessage: 'Invoice template created successfully.',
              dispatch,
            });
          }
        } catch (err) {
          const error = ensureApiError(err);
          const errorMessage = error.message;
          notify({
            status: 'error',
            errorMessage:
              errorMessage ||
              'Error occurs while creating the invoice template.',
            dispatch,
          });
        }
      },
    }),
    updateTemplate: build.mutation<InvoiceTemplate, UpdateInvoiceTemplateInput>(
      {
        query: (invoiceTemplate) => ({
          path: `/invoice-templates`,
          method: ApiMethods.put,
          options: {
            body: {
              ...invoiceTemplate,
              taxPercentage: Number(invoiceTemplate.taxPercentage),
              lineItems: formatLineItems(invoiceTemplate.lineItems),
            },
          },
        }),
        async onQueryStarted(_, { dispatch, queryFulfilled }) {
          try {
            const result = await queryFulfilled;

            dispatch(
              invoicesApi.util.updateQueryData(
                'listInvoiceTemplates',
                undefined,
                (draft) => {
                  const index = draft.findIndex(
                    (template) => template.id === result.data.id,
                  );
                  draft.splice(index, 1, result.data);
                },
              ),
            );
            if (result.data) {
              notify({
                status: 'success',
                successMessage: 'Invoice template updated successfully.',
                dispatch,
              });
            }
          } catch (err) {
            const error = ensureApiError(err);
            const errorMessage = error?.message;
            notify({
              status: 'error',
              errorMessage:
                errorMessage ||
                'Error occurs while updating the invoice template.',
              dispatch,
            });
          }
        },
      },
    ),

    listInvoiceTemplates: build.query<InvoiceTemplate[], void>({
      queryFn: async () => {
        // fetch invoice templates with pagination
        const response = await loadInvoiceTemplates();
        return { data: response.data || [] };
      },
    }),
    removeInvoiceTemplate: build.mutation<string, string>({
      query: (id) => ({
        path: `/invoice-templates/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          invoicesApi.util.updateQueryData(
            'listInvoiceTemplates',
            undefined,
            (draft) => draft.filter((template) => template.id !== id),
          ),
        );

        try {
          await queryFulfilled;
          notify({
            status: 'success',
            successMessage: 'Template deleted successfully.',
            dispatch,
          });
        } catch (error) {
          patch.undo();
          notify({
            status: 'error',
            errorMessage: 'A problem occurred while deleting the template.',
            error,
            dispatch,
          });
        }
      },
    }),
  }),
});

export const updateInvoiceQuery = (
  invoice: Invoice,
  dispatch: ThunkDispatch<any, any, AnyAction>,
  state: RootState,
) => {
  const clientInvoicePatches: PatchCollection[] = [];
  let companyInvoicePatch;

  const {
    recipientId,
    clientId: invoiceClientId,
    companyId: invoiceCompanyId,
  } = invoice;

  const { companies, clients } = state.clients;
  const companiesMap = arrayToMap(companies, 'id');
  // If found, this indicates that this is a company invoice
  const company = companiesMap.get(invoiceCompanyId || recipientId);

  if (company && !invoiceClientId) {
    // For company invoice, we need to update the client details page for all client for this company
    const companyClientsIds = clients
      .filter(
        (client) =>
          client.fields.companyId === company.id ||
          client.fields.companyIds?.includes(company.id),
      )
      .map((client) => client.id);

    for (const clientId of companyClientsIds) {
      clientInvoicePatches.push(
        dispatch(
          invoicesApi.util.updateQueryData(
            'getInvoicesByRecipientId',
            {
              clientId,
              companyId: company.id,
              includeCompanyInvoices: Boolean(clientId && company.id),
            },
            (draft) => {
              const index = draft.findIndex((i) => i.id === invoice.id);
              if (index == -1) {
                draft.unshift(invoice);
              } else {
                draft.splice(index, 1, invoice);
              }
            },
          ),
        ),
      );
    }

    companyInvoicePatch = dispatch(
      invoicesApi.util.updateQueryData(
        'getInvoicesByRecipientId',
        {
          clientId: undefined,
          companyId: company.id,
          includeCompanyInvoices: false,
        },
        (draft) => {
          const index = draft.findIndex((i) => i.id === invoice.id);
          if (index == -1) {
            draft.unshift(invoice);
          } else {
            draft.splice(index, 1, invoice);
          }
        },
      ),
    );
  } else {
    // Update the client details invoice list for that particular client
    if (invoiceClientId || recipientId) {
      const queryParams: LoadInvoicesQueryOptions = {
        clientId: invoiceClientId || recipientId,
        includeCompanyInvoices: true,
      };
      if (invoiceCompanyId) {
        queryParams.companyId = invoiceCompanyId;
      }
      clientInvoicePatches.push(
        dispatch(
          invoicesApi.util.updateQueryData(
            'getInvoicesByRecipientId',
            queryParams,
            (draft) => {
              const index = draft.findIndex((i) => i.id === invoice.id);
              if (index == -1) {
                draft.unshift(invoice);
              } else {
                draft.splice(index, 1, invoice);
              }
            },
          ),
        ),
      );
      companyInvoicePatch = dispatch(
        invoicesApi.util.updateQueryData(
          'getInvoicesByRecipientId',
          { companyId: invoiceCompanyId, includeCompanyInvoices: false },
          (draft) => {
            const index = draft.findIndex((i) => i.id === invoice.id);
            if (index == -1) {
              draft.unshift(invoice);
            } else {
              draft.splice(index, 1, invoice);
            }
          },
        ),
      );
    }
  }
  // Update the global invoice list
  const listInvoicesPatch = dispatch(
    invoicesApi.util.updateQueryData('getInvoices', undefined, (draft) => {
      if (invoice) {
        const index = draft.findIndex((i) => i.id === invoice.id);
        if (index == -1) {
          draft.unshift(invoice);
        } else {
          draft.splice(index, 1, invoice);
        }
      }
    }),
  );

  return { listInvoicesPatch, clientInvoicePatches, companyInvoicePatch };
};

// TODO(Neil); consolidate this into the above updateInvoiceQuery.
// The updateInvoiceQuery should not require the full state to be passed in.
// We should only need to update the queries based on the recipient.
// Ticket: https://linear.app/copilotplatforms/issue/POR-12428/conslidate-getinvoicesbyrecipientid
export const updateInvoice = (
  dispatch: AppDispatch,
  invoice: Invoice,
  recipientInfo: { companyId?: string; clientId?: string },
) => {
  let clientInvoicePatch;
  const { recipientId } = invoice;
  if (recipientId) {
    clientInvoicePatch = dispatch(
      invoicesApi.util.updateQueryData(
        'getInvoicesByRecipientId',
        { ...recipientInfo, includeCompanyInvoices: true },
        (draft) => {
          const index = draft.findIndex((i) => i.id === invoice.id);
          if (index == -1) {
            draft.unshift(invoice);
          } else {
            draft.splice(index, 1, invoice);
          }
        },
      ),
    );
  }
  // Update the global invoice list
  const listInvoicesPatch = dispatch(
    invoicesApi.util.updateQueryData('getInvoices', undefined, (draft) => {
      if (invoice) {
        const index = draft.findIndex((i) => i.id === invoice.id);
        if (index == -1) {
          draft.unshift(invoice);
        } else {
          draft.splice(index, 1, invoice);
        }
      }
    }),
  );

  return { listInvoicesPatch, clientInvoicePatch };
};

export const selectRecipientInfo = createSelector(
  [selectAllCompaniesMap, (_, recipientId: string) => recipientId],
  (companiesMap, recipientId) => {
    const company = companiesMap.get(recipientId);
    return company ? { companyId: recipientId } : { clientId: recipientId };
  },
);

export const {
  useDeleteInvoiceMutation,
  useVoidInvoiceMutation,
  useCreateInvoiceMutation,
  useGetInvoicesQuery,
  usePayInvoiceMutation,
  useGetInvoiceByIdQuery,
  useCreateInvoiceTemplateMutation,
  useUpdateTemplateMutation,
  useListInvoiceTemplatesQuery,
  useDuplicateInvoiceMutation,
  useRemoveInvoiceTemplateMutation,
  useGetInvoicesByRecipientIdQuery,
  useUpdateInvoiceMutation,
  useSendDraftInvoiceMutation,
  useGetInvoicesBySubscriptionIdQuery,
} = invoicesApi;
